import { Collapse } from '@mui/material';
import {
    FunctionComponent,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';

import { PlanningQuestion } from '../../../../types/work';
import QuestionsHeader from './QuestionsHeader';
import QuestionsList from './QuestionsList';
import { SectionRoot } from './styled';
import { Answer, FREE_TEXT_OPTION, initAnswers } from './types';

export type PlanningAnswerPayload = {
    questionId: string;
    selectedOptionIds: string[];
    freeText: string | null;
};

interface Props {
    questions: PlanningQuestion[];
    onSubmit: (answers: PlanningAnswerPayload[]) => void;
    isSubmitting?: boolean;
    collapsible?: boolean;
}

/**
 * Question cards + submit — mirrors
 * ``beehive-dashboards/.../PlanningQuestions``.
 */
const PlanningQuestionsForm: FunctionComponent<Props> = ({
    questions,
    onSubmit,
    isSubmitting = false,
    collapsible = false
}) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const isMountedRef = useRef(false);

    useEffect(() => {
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            return;
        }
        rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [ questions.length ]);

    const [ expanded, setExpanded ] = useState(true);
    const [ expandedQuestions, setExpandedQuestions ] = useState<Set<number>>(
        new Set(questions.map((_, idx) => idx))
    );
    const [ skipped, setSkipped ] = useState<Set<number>>(new Set());
    const [ answers, setAnswers ] = useState<Record<number, Answer>>(() =>
        initAnswers(questions)
    );

    useEffect(() => {
        setAnswers(initAnswers(questions));
        setExpandedQuestions(new Set(questions.map((_, idx) => idx)));
        setSkipped(new Set());
    }, [ questions ]);

    const handleToggleExpanded = useCallback(
        () => setExpanded((prev) => !prev),
        []
    );

    const toggleQuestion = useCallback(
        (idx: number) => {
            setExpandedQuestions((prev) => {
                const next = new Set(prev);
                if (next.has(idx)) {
                    next.delete(idx);
                } else {
                    next.add(idx);
                }
                return next;
            });
            if (skipped.has(idx)) {
                setSkipped((prev) => {
                    const next = new Set(prev);
                    next.delete(idx);
                    return next;
                });
            }
        },
        [ skipped ]
    );

    const handleSkip = useCallback((idx: number) => {
        setSkipped((prev) => {
            const next = new Set(prev);
            next.add(idx);
            return next;
        });
        setExpandedQuestions((prev) => {
            const next = new Set(prev);
            next.delete(idx);
            return next;
        });
        setAnswers((prev) => ({
            ...prev,
            [idx]: { selected: new Set<string>(), freeText: '' }
        }));
    }, []);

    const handleSingleChange = useCallback((qIdx: number, value: string) => {
        setAnswers((prev) => ({
            ...prev,
            [qIdx]: { ...prev[qIdx], selected: new Set([ value ]) }
        }));
    }, []);

    const handleMultiToggle = useCallback((qIdx: number, value: string) => {
        setAnswers((prev) => {
            const current = new Set(prev[qIdx].selected);
            if (current.has(value)) {
                current.delete(value);
            } else {
                current.add(value);
            }
            return { ...prev, [qIdx]: { ...prev[qIdx], selected: current } };
        });
    }, []);

    const handleFreeTextChange = useCallback((qIdx: number, value: string) => {
        setAnswers((prev) => ({
            ...prev,
            [qIdx]: { ...prev[qIdx], freeText: value }
        }));
    }, []);

    const handleSubmit = useCallback(() => {
        const formatted: PlanningAnswerPayload[] = questions.map((_, idx) => {
            const answer = answers[idx];
            const selectedOptionIds = Array.from(answer.selected).filter(
                (v) => v !== FREE_TEXT_OPTION
            );
            const freeText = answer.selected.has(FREE_TEXT_OPTION)
                ? answer.freeText
                : null;
            return {
                questionId: String(idx),
                selectedOptionIds,
                freeText
            };
        });
        onSubmit(formatted);
    }, [ questions, answers, onSubmit ]);

    return (
        <SectionRoot ref={rootRef}>
            <QuestionsHeader
                questions={questions}
                collapsible={collapsible}
                expanded={expanded}
                onToggleExpanded={handleToggleExpanded}
            />
            <Collapse in={!collapsible || expanded}>
                <QuestionsList
                    questions={questions}
                    answers={answers}
                    expandedQuestions={expandedQuestions}
                    skipped={skipped}
                    onToggleQuestion={toggleQuestion}
                    onSkip={handleSkip}
                    onSingleChange={handleSingleChange}
                    onMultiToggle={handleMultiToggle}
                    onFreeTextChange={handleFreeTextChange}
                    onSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                />
            </Collapse>
        </SectionRoot>
    );
};

export default PlanningQuestionsForm;
